/// Renders GeoJSON Polygon / MultiPolygon features as filled land
/// regions on the radar. Coastline is implied by stroking the same
/// polygons at a slightly brighter color.
///
/// One Pixi.Graphics object per feature collection; we redraw only when
/// the source changes (which on MVP is "once on load").

import { Container, Graphics } from 'pixi.js'
import type { CoordinateProjector } from '../geo/CoordinateProjector'
import type {
  AnyGeometry,
  FeatureCollection,
  Polygon,
  MultiPolygon,
  Position,
} from '../types/GeoJSON'
import { radarTheme } from './theme'

export class BasemapLayer extends Container {
  constructor(features: FeatureCollection, projector: CoordinateProjector) {
    super()
    const g = new Graphics()
    this.addChild(g)

    for (const feat of features.features) {
      if (!feat.geometry) continue
      drawGeometry(g, feat.geometry, projector)
    }

    g.fill({ color: radarTheme.land, alpha: 1 })
    g.stroke({ width: 0.6, color: radarTheme.landStroke, alpha: 0.9 })
  }
}

function drawGeometry(
  g: Graphics,
  geom: AnyGeometry,
  projector: CoordinateProjector,
): void {
  switch (geom.type) {
    case 'Polygon':
      drawPolygon(g, geom, projector)
      break
    case 'MultiPolygon':
      drawMultiPolygon(g, geom, projector)
      break
    default:
      // Lines aren't part of the basemap; skip silently.
      break
  }
}

function drawPolygon(
  g: Graphics,
  poly: Polygon,
  projector: CoordinateProjector,
): void {
  for (const ring of poly.coordinates) {
    drawRing(g, ring, projector)
  }
}

function drawMultiPolygon(
  g: Graphics,
  mp: MultiPolygon,
  projector: CoordinateProjector,
): void {
  for (const poly of mp.coordinates) {
    for (const ring of poly) {
      drawRing(g, ring, projector)
    }
  }
}

function drawRing(
  g: Graphics,
  ring: Position[],
  projector: CoordinateProjector,
): void {
  if (ring.length < 3) return
  let started = false
  for (const pos of ring) {
    const [lon, lat] = pos
    const p = projector.project({ latitude: lat, longitude: lon }, true)
    if (!started) {
      g.moveTo(p.x, p.y)
      started = true
    } else {
      g.lineTo(p.x, p.y)
    }
  }
  g.closePath()
}
